import React, { useState, useEffect } from 'react';
import { TextField, InputAdornment, TextFieldProps } from '@mui/material';

interface PriceInputProps {
  value: number | null | string;
  onChange: (value: number | null | string) => void;
  label?: string;
  helperText?: string;
  currency?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  variant?: 'filled' | 'outlined' | 'standard';
  placeholder?: string;
}

/**
 * Shared price/currency input component with validation
 * - Accepts comma (,) or dot (.) as decimal separator
 * - Only allows numeric input with optional decimal
 * - Parses and formats value on blur
 * - Can be used for prices, amounts, limits, etc.
 */
const PriceInput: React.FC<PriceInputProps> = ({
  value,
  onChange,
  label = 'Montant',
  helperText,
  currency = 'MAD',
  required = false,
  disabled = false,
  error = false,
  fullWidth = true,
  size = 'small',
  variant = 'filled',
  placeholder,
}) => {
  // Handle change - only allow valid numeric patterns
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    if (inputValue === '') {
      onChange(null);
    } else if (/^\d*[.,]?\d*$/.test(inputValue)) {
      // Allow digits with optional comma or dot as decimal separator
      onChange(inputValue);
    }
    // Invalid input is ignored
  };

  // Handle blur - parse and format the value
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    if (inputValue && inputValue !== '') {
      // Replace comma with dot for parsing
      const numValue = parseFloat(inputValue.replace(',', '.'));

      if (!isNaN(numValue)) {
        onChange(numValue);
      } else {
        onChange(null);
      }
    }
  };

  // Format display value
  const displayValue = value ?? '';

  return (
    <TextField
      variant={variant}
      label={label}
      type="text"
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      required={required}
      disabled={disabled}
      error={error}
      fullWidth={fullWidth}
      size={size}
      placeholder={placeholder}
      InputProps={{
        endAdornment: currency ? (
          <InputAdornment position="end">{currency}</InputAdornment>
        ) : undefined,
      }}
      helperText={helperText}
    />
  );
};

/**
 * Utility function to parse price value for submission
 * Use this when preparing data for API calls
 */
export const parsePriceValue = (value: number | null | string): number => {
  if (typeof value === 'string') {
    const parsed = parseFloat(value.replace(',', '.'));
    return isNaN(parsed) ? 0 : Math.round(parsed * 100) / 100;
  }
  return value ?? 0;
};

export default PriceInput;
